import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import isEmail from 'validator/lib/isEmail';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import styles from './ContactsExportModal.module.css';
import classNames from 'classnames';
import {
  CloseOutlined,
  InfoCircleOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { Button, Checkbox, Input, SearchInput } from '@mm/components';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { useDebounce } from '@mm/hooks';
import { ExportableContactField } from '@mm/state/controllers/ContactsCotroller';
import { GoogleSheetPickerForm } from '@mm/stateful-components/ContactsExportModal/GoogleSheetPickerForm/GoogleSheetPickerForm';
import { IGoogleSheetPickerProps } from '@mm/state/modules/modalsState/props';
import { Radio } from 'antd';

type Props = {
  className?: string;
  audienceId?: number;
  contactsCount?: number;
  onSuccess?: () => void;
};

type Checkbox = {
  name: string;
  value: string;
  checked: boolean;
  visible: boolean;
};

type ExportOption = 'csv' | 'google_sheets';

const MainAttributes = [
  'First name',
  'Last name',
  'Email',
  'Email domain',
  'Landing page url',
  'Time on site',
  'Web page views',
  'Num of clicks',
  'Scroll depth',
];

const RADIO_OPTIONS = [
  {
    label: (
      <>
        <span>Export to CSV </span>
        <span className={styles.notation}>
          Each file has a limit of 5K contacts
        </span>
      </>
    ),
    value: 'csv',
  },
  {
    label: 'Export to Google Sheets',
    value: 'google_sheets',
  },
];
export const ContactsExportModal = (
  props: Props & { closeModal: () => void },
) => {
  const { className, closeModal, onSuccess, audienceId, contactsCount } = props;
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const botId = useSelector(activeChatbotIdSelector);
  const { createScheduledExport, getExportableFields } =
    useContactsCRUDActions();
  const { createScheduledExport: exportAudience } = useAudiencesCRUDActions();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [exportType, setExportType] = useState<ExportOption>('csv');
  const [googleSheetProps, setGoogleSheetProps] =
    useState<IGoogleSheetPickerProps>();
  const debouncedEmail = useDebounce(email.trim(), 300);
  const [searchString, setSearchString] = useState('');

  const attributesToCheckboxes = (
    fieldsList: ExportableContactField[],
  ): Checkbox[] =>
    fieldsList
      .filter(x => MainAttributes.includes(x.name))
      .concat(fieldsList.filter(x => !MainAttributes.includes(x.name)))
      .map(attribute => ({
        name: attribute.name,
        value: attribute.value,
        checked: MainAttributes.includes(attribute.name),
        visible: true,
      }));

  const [checkboxes, setCheckboxes] = useState<Checkbox[]>([]);
  const scheduleExport = async (onSuccess?: () => void) => {
    const emailAddress = email && email.trim();
    const checkedFields = getChecked();
    const exportFields =
      checkedFields.length === checkboxes.length ? undefined : checkedFields;

    if (isEmail(emailAddress) || !!googleSheetProps?.sheet) {
      if (facebookPageId && !audienceId) {
        await createScheduledExport(
          facebookPageId,
          emailAddress,
          exportFields,
          googleSheetProps,
        );
        onSuccess?.();
      } else if (botId && audienceId) {
        await exportAudience(
          botId,
          audienceId,
          emailAddress,
          exportFields,
          googleSheetProps,
        );
        onSuccess?.();
      }
      closeModal();
    }
  };

  const toggleCheckbox = (checkboxValue: string) => {
    setCheckboxes(prevCheckboxes =>
      prevCheckboxes.map(checkbox =>
        checkbox.value === checkboxValue
          ? { ...checkbox, checked: !checkbox.checked }
          : checkbox,
      ),
    );
  };

  const toggleOnlyMainAttributesCheckboxes = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { checked } = e.target;

    setCheckboxes(prevCheckboxes =>
      prevCheckboxes.map(checkbox => ({
        ...checkbox,
        checked: MainAttributes.includes(checkbox.name)
          ? checked
          : checkbox.checked,
      })),
    );
  };

  const getChecked = () =>
    checkboxes
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value);

  const filterCheckboxes = (searchString: string) => {
    setCheckboxes(prevCheckboxes =>
      prevCheckboxes.map(checkbox => ({
        ...checkbox,
        visible:
          searchString.length === 0 ||
          checkbox.name.toLowerCase().includes(searchString.toLowerCase()),
      })),
    );
  };

  const isChecked = (value: ExportOption) => value === exportType;

  const onSetExportType = (value: ExportOption) => {
    setExportType(value);
    if (value === 'csv') {
      setGoogleSheetProps(undefined);
    } else {
      setEmail('');
    }
  };

  useEffect(() => {
    if (debouncedEmail) {
      if (isEmail(debouncedEmail)) {
        setError('');
      } else {
        setError('Please enter a valid email.');
      }
    }
  }, [debouncedEmail]);

  useEffect(() => filterCheckboxes(searchString), [searchString]);

  useEffect(() => {
    const fetchExportableFields = async () => {
      const exportableFields = await getExportableFields(facebookPageId);
      setCheckboxes(attributesToCheckboxes(exportableFields));
    };
    fetchExportableFields();
  }, []);

  return (
    <div className={classNames(styles.container, className)}>
      <CloseOutlined className={styles.closeIcon} onClick={closeModal} />

      <div className={styles.header}>
        <div className={styles.title}>
          Exporting {contactsCount?.toLocaleString('en-US')}{' '}
          {contactsCount === 1 ? 'lead' : 'leads'}
        </div>
      </div>
      <section className={styles.export}>
        <label className={styles.export_label}>
          Choose a way to export your leads:
        </label>
        {RADIO_OPTIONS.map(option => (
          <Radio
            key={option.value}
            name="export-leads"
            checked={isChecked(option.value as ExportOption)}
            value={option.value}
            className={styles.radioInput}
            onChange={e => onSetExportType(e.target.value as ExportOption)}
          >
            {option.label}
          </Radio>
        ))}
      </section>
      {exportType === 'csv' ? (
        <>
          <div className={styles.info}>
            <InfoCircleOutlined className={styles.infoIcon} />

            <div className={styles.content}>
              <div className={styles.infoTitle}>
                Exporting leads to CSV could take some time.
              </div>
              <div className={styles.description}>
                We will email you when it’s ready.
              </div>
            </div>
          </div>

          <div className={styles.formSection}>
            Enter the email address you’d like us to send your download link to
            <Input
              css={{
                input: styles.audienceEditBarInput,
                container: styles.audienceEditBar,
              }}
              value={email}
              onChange={e => setEmail(e.target.value)}
              invalid={!!error}
              placeholder="Enter email address"
            />
            {error && <div className={styles.error}>{error}</div>}
          </div>
        </>
      ) : (
        <GoogleSheetPickerForm setGoogleSheetProps={setGoogleSheetProps} />
      )}

      <div className={styles.formSection}>
        Choose attributes:
        <div className={styles.searchInput}>
          <SearchInput
            css={{
              input: styles.audienceEditBarInput,
              container: styles.audienceEditBar,
            }}
            value={searchString}
            onChange={e => setSearchString(e.target.value)}
            placeholder="Search attribute"
          />
        </div>
        <div className={styles.checkboxesWrapper}>
          <div className={styles.checkboxes}>
            {searchString.length === 0 && (
              <Checkbox
                isChecked={checkboxes
                  .filter(checkbox => MainAttributes.includes(checkbox.name))
                  .every(checkbox => checkbox.checked)}
                label="Main Attributes"
                labelClassName={classNames(
                  styles.checkboxLabel,
                  styles.checkboxLabel_bold,
                )}
                onToggle={checked =>
                  toggleOnlyMainAttributesCheckboxes(checked)
                }
              />
            )}
            {checkboxes
              .filter(checkbox => checkbox.visible)
              .map(checkbox => (
                <Checkbox
                  key={checkbox.name}
                  isChecked={checkbox.checked}
                  label={checkbox.name}
                  labelClassName={styles.checkboxLabel}
                  onToggle={() => toggleCheckbox(checkbox.value)}
                />
              ))}
          </div>
        </div>
      </div>

      <div className={styles.buttons}>
        <Button
          onClick={() => scheduleExport(onSuccess)}
          css={{ container: styles.primaryBtn }}
          disabled={
            !!error ||
            !(email || googleSheetProps?.sheet) ||
            !checkboxes.some(checkbox => checkbox.checked)
          }
        >
          <SendOutlined className={styles.arrow} />
          Send
        </Button>
      </div>
    </div>
  );
};
